import type {
  QuoteSnapshot,
  SnapshotCompareResult,
  SnapshotDiffDirection,
  SnapshotDiffField,
  SnapshotItem,
  SnapshotItemCompare,
} from './types'

const NUMERIC_FIELDS: Array<{ key: keyof SnapshotItem; label: string }> = [
  { key: 'purchasePrice', label: 'Preço Compra' },
  { key: 'salePrice', label: 'Preço Venda' },
  { key: 'profit', label: 'Lucro' },
  { key: 'margin', label: 'Margem' },
  { key: 'quantity', label: 'Quantidade' },
]

/**
 * SnapshotCompare — pure diff between two immutable snapshots.
 */
export class SnapshotCompare {
  compare(left: QuoteSnapshot, right: QuoteSnapshot): SnapshotCompareResult {
    const leftMap = new Map(left.items.map((item) => [this.itemKey(item), item]))
    const rightMap = new Map(right.items.map((item) => [this.itemKey(item), item]))
    const keys = new Set([...leftMap.keys(), ...rightMap.keys()])

    const items: SnapshotItemCompare[] = []
    let unchangedItems = 0
    let changedItems = 0
    let addedItems = 0
    let removedItems = 0

    for (const key of keys) {
      const leftItem = leftMap.get(key) ?? null
      const rightItem = rightMap.get(key) ?? null

      if (leftItem && !rightItem) {
        removedItems += 1
        items.push({
          key,
          description: leftItem.description,
          productId: leftItem.productId,
          leftItemId: leftItem.id,
          rightItemId: null,
          fields: this.diffItems(leftItem, null),
          hasChanges: true,
        })
        continue
      }

      if (!leftItem && rightItem) {
        addedItems += 1
        items.push({
          key,
          description: rightItem.description,
          productId: rightItem.productId,
          leftItemId: null,
          rightItemId: rightItem.id,
          fields: this.diffItems(null, rightItem),
          hasChanges: true,
        })
        continue
      }

      const fields = this.diffItems(leftItem, rightItem)
      const hasChanges = fields.some((f) => f.direction !== 'same')
      if (hasChanges) changedItems += 1
      else unchangedItems += 1

      items.push({
        key,
        description: rightItem?.description ?? leftItem?.description ?? '',
        productId: rightItem?.productId ?? leftItem?.productId ?? null,
        leftItemId: leftItem?.id ?? null,
        rightItemId: rightItem?.id ?? null,
        fields,
        hasChanges,
      })
    }

    return {
      left,
      right,
      items,
      summary: { changedItems, unchangedItems, addedItems, removedItems },
    }
  }

  priceIndicator(oldPrice: number, newPrice: number) {
    const delta = round2(newPrice - oldPrice)
    const deltaPercent = oldPrice === 0 ? (newPrice === 0 ? 0 : 100) : round2((delta / oldPrice) * 100)
    return {
      oldPrice: round2(oldPrice),
      currentPrice: round2(newPrice),
      difference: delta,
      differencePercent: deltaPercent,
      direction: directionOf(delta),
    }
  }

  private itemKey(item: SnapshotItem): string {
    if (item.productId) return `p:${item.productId}`
    return `d:${item.description.trim().toLowerCase()}`
  }

  private diffItems(left: SnapshotItem | null, right: SnapshotItem | null): SnapshotDiffField[] {
    const fields: SnapshotDiffField[] = []

    const leftSupplier = left?.supplier ?? null
    const rightSupplier = right?.supplier ?? null
    fields.push({
      field: 'supplier',
      label: 'Fornecedor',
      oldValue: leftSupplier,
      newValue: rightSupplier,
      delta: null,
      deltaPercent: null,
      direction: leftSupplier === rightSupplier ? 'same' : leftSupplier == null ? 'up' : rightSupplier == null ? 'down' : 'up',
    })

    for (const { key, label } of NUMERIC_FIELDS) {
      const oldValue = left ? Number(left[key]) : null
      const newValue = right ? Number(right[key]) : null
      const delta =
        oldValue != null && newValue != null ? round2(newValue - oldValue) : oldValue == null && newValue != null ? newValue : null
      const deltaPercent =
        oldValue != null && newValue != null && oldValue !== 0
          ? round2(((newValue - oldValue) / oldValue) * 100)
          : oldValue === 0 && newValue != null && newValue !== 0
            ? 100
            : oldValue != null && newValue != null
              ? 0
              : null

      fields.push({
        field: key,
        label,
        oldValue,
        newValue,
        delta,
        deltaPercent,
        direction: directionOf(delta ?? (oldValue == null && newValue != null ? 1 : newValue == null && oldValue != null ? -1 : 0)),
      })
    }

    return fields
  }
}

function directionOf(delta: number): SnapshotDiffDirection {
  if (delta > 0) return 'up'
  if (delta < 0) return 'down'
  return 'same'
}

function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100
}
